import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, MessageSquare, Smartphone, Send, Plus, Trash2, X, Bell } from "lucide-react";

const CHANNELS = [
  { v: "email", icon: Mail, l: "Email" },
  { v: "sms", icon: MessageSquare, l: "SMS" },
  { v: "whatsapp", icon: MessageSquare, l: "WhatsApp" },
  { v: "push", icon: Bell, l: "Push" },
];

export default function AdminComms() {
  const [tab, setTab] = useState("templates");
  return (
    <AdminLayout subtitle="Module 13 · Communication Center" title="Templates & Broadcasts">
      <div className="flex flex-wrap gap-px bg-white/10 mb-6" data-testid="comms-tabs">
        {[["templates", "Templates"], ["broadcast", "Broadcast"], ["history", "History"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`flex-1 min-w-[140px] px-4 py-3 text-sm bg-ec-ink transition-colors ${tab === v ? "border-b-2 border-ec-emerald text-white font-bold" : "text-zinc-400 hover:bg-white/5"}`} data-testid={`comms-tab-${v}`}>{l}</button>
        ))}
      </div>
      {tab === "templates" && <TemplatesTab />}
      {tab === "broadcast" && <BroadcastTab />}
      {tab === "history" && <HistoryTab />}
    </AdminLayout>
  );
}

function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [channel, setChannel] = useState("");
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { try { setRows((await api.get("/admin/comm/templates", { params: { channel } })).data.rows); } catch (e) {} }, [channel]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!confirm("Delete this template?")) return;
    try { await api.delete(`/admin/comm/templates/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="templates-tab">
      <div className="flex justify-between items-center mb-4">
        <select value={channel} onChange={(e) => setChannel(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="templates-channel-filter">
          <option value="" className="bg-ec-ink">All channels</option>
          {CHANNELS.map((c) => <option key={c.v} value={c.v} className="bg-ec-ink">{c.l}</option>)}
        </select>
        <Button onClick={() => setShowNew(true)} className="rounded-sm bg-ec-emerald text-ec-ink gap-2" data-testid="template-new"><Plus className="w-4 h-4" />New template</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4" data-testid="templates-grid">
        {rows.map((t) => {
          const Ch = CHANNELS.find((c) => c.v === t.channel);
          const Icon = Ch?.icon || Mail;
          return (
            <Card key={t.id} className="rounded-sm border-white/10 bg-white/[0.02]" data-testid={`template-${t.channel}-${t.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-white/10 border-white/10 rounded-sm gap-1"><Icon className="w-3 h-3" />{t.channel}</Badge>
                  <span className="font-mono-plex text-[10px] text-zinc-500">{t.key}</span>
                </div>
                <div className="font-display font-bold text-white">{t.name}</div>
                {t.subject && <div className="text-xs text-zinc-300 mt-1">{t.subject}</div>}
                <pre className="text-[11px] font-mono-plex text-zinc-400 mt-2 whitespace-pre-wrap line-clamp-4 bg-black/30 p-2 rounded-sm">{t.body}</pre>
                <div className="flex justify-end gap-2 mt-3">
                  <button onClick={() => setEdit(t)} className="px-2 py-1 text-xs border border-white/10 rounded-sm text-white hover:bg-white/10" data-testid={`template-edit-${t.id}`}>Edit</button>
                  <button onClick={() => del(t.id)} className="p-1.5 border border-ec-error/30 text-ec-error rounded-sm hover:bg-ec-error/10" data-testid={`template-delete-${t.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {(showNew || edit) && <TemplateModal initial={edit} isNew={showNew && !edit} onClose={() => { setShowNew(false); setEdit(null); }} onSaved={() => { setShowNew(false); setEdit(null); load(); }} />}
    </div>
  );
}

const EMPTY_TPL = { channel: "email", key: "", name: "", subject: "", body: "", variables: [] };

function TemplateModal({ initial, isNew, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY_TPL, ...initial, variables: (initial?.variables || []).join(", ") });
  const save = async () => {
    try {
      const payload = { ...f, variables: (f.variables || "").split(",").map((s) => s.trim()).filter(Boolean) };
      if (isNew) await api.post("/admin/comm/templates", payload);
      else await api.patch(`/admin/comm/templates/${initial.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="template-modal">
      <div className="bg-ec-ink border border-white/10 rounded-sm max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl font-bold text-white">{isNew ? "New template" : "Edit template"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="eyebrow text-zinc-500">Channel</Label>
            <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} className="rounded-sm h-10 px-3 bg-white/5 border border-white/10 text-white text-sm w-full mt-2" disabled={!isNew} data-testid="template-input-channel">
              {CHANNELS.map((c) => <option key={c.v} value={c.v} className="bg-ec-ink">{c.l}</option>)}
            </select>
          </div>
          <div>
            <Label className="eyebrow text-zinc-500">Key</Label>
            <Input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2 font-mono-plex" data-testid="template-input-key" disabled={!isNew} />
          </div>
          <div className="sm:col-span-2">
            <Label className="eyebrow text-zinc-500">Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="template-input-name" />
          </div>
          {f.channel === "email" && (
            <div className="sm:col-span-2">
              <Label className="eyebrow text-zinc-500">Subject</Label>
              <Input value={f.subject || ""} onChange={(e) => setF({ ...f, subject: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="template-input-subject" />
            </div>
          )}
          <div className="sm:col-span-2">
            <Label className="eyebrow text-zinc-500">Body (use {`{{var}}`} placeholders)</Label>
            <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={7} className="rounded-sm w-full mt-2 p-3 bg-white/5 border border-white/10 text-white text-sm font-mono-plex" data-testid="template-input-body" />
          </div>
          <div className="sm:col-span-2">
            <Label className="eyebrow text-zinc-500">Variables (comma-separated)</Label>
            <Input value={f.variables} onChange={(e) => setF({ ...f, variables: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2 font-mono-plex" data-testid="template-input-variables" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" className="rounded-sm border-white/10 text-white" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-ec-emerald text-ec-ink" data-testid="template-save">Save template</Button>
        </div>
      </div>
    </div>
  );
}

function BroadcastTab() {
  const [f, setF] = useState({ channel: "email", audience: "all_users", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!confirm(`Send broadcast to ${f.audience} via ${f.channel}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/comm/broadcast", { ...f, selected_user_ids: [] });
      toast.success(`Sent to ${data.recipients} recipients${data.mocked ? " (mocked)" : ""}`);
      setF({ ...f, subject: "", body: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Card className="rounded-sm border-white/10 bg-white/[0.02] max-w-3xl" data-testid="broadcast-form">
      <CardContent className="p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="eyebrow text-zinc-500">Channel</Label>
            <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} className="rounded-sm h-10 px-3 bg-white/5 border border-white/10 text-white text-sm w-full mt-2" data-testid="broadcast-channel">
              {CHANNELS.map((c) => <option key={c.v} value={c.v} className="bg-ec-ink">{c.l}</option>)}
            </select>
          </div>
          <div>
            <Label className="eyebrow text-zinc-500">Audience</Label>
            <select value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} className="rounded-sm h-10 px-3 bg-white/5 border border-white/10 text-white text-sm w-full mt-2" data-testid="broadcast-audience">
              {[["all_users", "All users"], ["professionals", "Professionals"], ["organizations", "Organizations"], ["institutions", "Institutions"]].map(([v, l]) => <option key={v} value={v} className="bg-ec-ink">{l}</option>)}
            </select>
          </div>
        </div>
        {f.channel === "email" && (
          <div>
            <Label className="eyebrow text-zinc-500">Subject</Label>
            <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="broadcast-subject" />
          </div>
        )}
        <div>
          <Label className="eyebrow text-zinc-500">Message</Label>
          <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={6} className="rounded-sm w-full mt-2 p-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="broadcast-body" />
        </div>
        <div className="text-[11px] text-zinc-500 font-mono-plex">
          Email + push are delivered as in-app notifications immediately. SMS + WhatsApp delivery is MOCKED — real send wired when Twilio + WhatsApp Business creds are connected.
        </div>
        <div className="flex justify-end">
          <Button onClick={send} disabled={sending || !f.body} className="rounded-sm bg-ec-emerald text-ec-ink gap-2" data-testid="broadcast-send"><Send className="w-4 h-4" />{sending ? "Sending…" : "Send broadcast"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function HistoryTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/comm/broadcasts").then((r) => setRows(r.data.rows)); }, []);
  return (
    <Card className="rounded-sm border-white/10 bg-white/[0.02]" data-testid="history-tab">
      <CardContent className="p-0">
        {rows.length === 0 ? <div className="py-10 text-center text-zinc-500" data-testid="history-empty">No broadcasts yet.</div> : (
          <ul className="divide-y divide-white/5">
            {rows.map((b) => (
              <li key={b.id} className="p-5" data-testid={`broadcast-row-${b.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-white/10 border-white/10 rounded-sm">{b.channel}</Badge>
                  <Badge className="bg-white/10 border-white/10 rounded-sm">{b.audience}</Badge>
                  <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40 rounded-sm">{b.recipient_count} recipients</Badge>
                  <span className="text-[10px] font-mono-plex text-zinc-500 ml-auto">{b.created_at}</span>
                </div>
                {b.subject && <div className="font-semibold text-white mt-2">{b.subject}</div>}
                <p className="text-sm text-zinc-300 mt-1">{b.body}</p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
